import {
	Link as RouterLink,
	type LinkProps as RouterLinkProps,
} from "react-router-dom";
import type {
	AnchorHTMLAttributes,
	ForwardedRef,
	ReactNode,
} from "react";
import { forwardRef } from "react";

type Href =
	| string
	| Readonly<{
			pathname?: string;
			query?: Readonly<Record<string, string | number | boolean | null | undefined>>;
			hash?: string;
	  }>;

export interface LinkProps<_Route extends string = string>
	extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
	readonly href: Href;
	readonly replace?: boolean;
	readonly scroll?: boolean;
	readonly prefetch?: boolean | null;
	readonly children?: ReactNode;
}

function hrefToRouterTarget(href: Href): RouterLinkProps["to"] {
	if (typeof href === "string") return href;
	const pathname = href.pathname ?? "/";
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(href.query ?? {})) {
		if (value !== null && value !== undefined) search.set(key, String(value));
	}
	const query = search.toString();
	return {
		pathname,
		search: query.length > 0 ? `?${query}` : "",
		hash: href.hash ?? "",
	};
}

function LinkImplementation(
	{
		href,
		replace = false,
		scroll: _scroll,
		prefetch: _prefetch,
		...anchorProps
	}: LinkProps,
	ref: ForwardedRef<HTMLAnchorElement>,
) {
	return (
		<RouterLink
			ref={ref}
			to={hrefToRouterTarget(href)}
			replace={replace}
			{...anchorProps}
		/>
	);
}

const Link = forwardRef(LinkImplementation);
Link.displayName = "ArtistRouterLink";

export default Link;
